import { useEffect, useMemo, useRef, useState } from "react";
import { SelectItemDto } from "../../types/SelectItemDto";
import "./AppMultiSelect.css";
import { useTranslation } from "react-i18next";

interface AppMultiSelectProps {
    options: SelectItemDto[];
    selectedValues: number[];
    onChange: (values: number[]) => void;
    placeholder?: string;
    disabled?: boolean;
    isInvalid?: boolean;
}

export default function AppMultiSelect({
    options,
    selectedValues,
    onChange,
    placeholder,
    disabled = false,
    isInvalid = false
}: AppMultiSelectProps) {
    const { t } = useTranslation(['common']);
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const selectedOptions = useMemo(
        () => options.filter((option) => selectedValues.includes(option.id)),
        [options, selectedValues]
    );

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const toggleValue = (value: number) => {
        const isSelected = selectedValues.includes(value);
        const newValues = isSelected
            ? selectedValues.filter((item) => item !== value)
            : [...selectedValues, value];
        onChange(newValues);
    };

    return (
        <div
            ref={containerRef}
            className={`app-multi-select ${disabled ? "disabled" : ""}`}
        >
            <div
                className={`app-multi-select__bar ${isInvalid ? "is-invalid" : ""}`}
                onClick={() => !disabled && setIsOpen((prev) => !prev)}
                aria-expanded={isOpen}
                role="button"
                tabIndex={disabled ? -1 : 0}
                onKeyDown={(event) => {
                    if (disabled) return;
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setIsOpen((prev) => !prev);
                    }
                }}
            >
                <div className="app-multi-select__selected-items">
                    {selectedOptions.length === 0 && (
                        <span className="app-multi-select__placeholder">{placeholder ?? t("common:selectDefaultText")}</span>
                    )}
                    {selectedOptions.map((option) => (
                        <span key={option.id} className="app-multi-select__tag">
                            {option.name}
                            <button
                                type="button"
                                className="app-multi-select__tag-remove"
                                onClick={(event) => {
                                    event.stopPropagation();
                                    toggleValue(option.id);
                                }}
                                disabled={disabled}
                            >
                                x
                            </button>
                        </span>
                    ))}
                </div>
                <span className="app-multi-select__caret">{isOpen ? "▴" : "▾"}</span>
            </div>

            {isOpen && !disabled && (
                <div className="app-multi-select__dropdown">
                    {options.map((option) => {
                        const value = option.id;
                        const isSelected = selectedValues.includes(value);

                        return (
                            <button
                                key={option.id}
                                type="button"
                                className={`app-multi-select__option ${isSelected ? "selected" : ""}`}
                                onClick={() => toggleValue(value)}
                            >
                                {option.name}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
